import { message } from "antd";
import axios from "axios";
import { Field, Form, Formik } from "formik";
import React from "react";
import { ratingHelpers } from "../../../helpers/commonHelpers";
import { ReviewV1 } from "../../../model/Reviews";
import { SelectItem } from "../../../model/views/Select.props";
import { EBTextAreaV2 } from "../Form/FormElements";
import { EBSelect } from "../eb-ui-components";
import { reloadWindowCallback } from "../reloadCallback";
import { EBLogger } from "../../../helpers/EBLogger";

export enum AskForImprovementsCondition {
  ALWAYS = "ALWAYS",
  WHEN_NOT_5_STAR = "WHEN_NOT_5_STAR",
  NEVER = "NEVER",
}

export enum ImprovementsRequiredCondition {
  ALWAYS = "ALWAYS",
  WHEN_NOT_5_STAR = "WHEN_NOT_5_STAR",
  NEVER = "NEVER",
}

export interface ImprovementsConditions {
  askCondition?: AskForImprovementsCondition;
  requiredCondition?: ImprovementsRequiredCondition;
  minLength?: number;
  maxLength?: number;
}

interface ReviewEditProps {
  currentValues?: ReviewV1;
  improvementsConditions?: ImprovementsConditions;
  namePrefix?: string;
}

function mapRatingSelectToAPI(selectVal: SelectItem) {
  return selectVal.value;
}

function mapRatingAPIToSelect(apiVal: number) {
  if (!apiVal) {
    return apiVal;
  }
  let selectValue = ratingHelpers.getDisplayRatingForValue(apiVal);
  return selectValue;
}

export function ReviewEdit(props: ReviewEditProps) {
  let shouldAskForImprovements =
    props.improvementsConditions?.askCondition ==
      AskForImprovementsCondition.ALWAYS ||
    props.improvementsConditions?.requiredCondition ==
      ImprovementsRequiredCondition.ALWAYS ||
    (props.improvementsConditions?.askCondition ==
      AskForImprovementsCondition.WHEN_NOT_5_STAR &&
      props.currentValues &&
      props.currentValues.starRatingFive &&
      props.currentValues.starRatingFive !== 5) ||
    (props.improvementsConditions?.requiredCondition ==
      ImprovementsRequiredCondition.WHEN_NOT_5_STAR &&
      props.currentValues &&
      props.currentValues.starRatingFive &&
      props.currentValues.starRatingFive !== 5);

  let areRequiredImprovements: boolean | undefined = false;
  if (
    props.improvementsConditions?.requiredCondition ==
      ImprovementsRequiredCondition.ALWAYS ||
    (props.improvementsConditions?.requiredCondition ==
      ImprovementsRequiredCondition.WHEN_NOT_5_STAR &&
      props.currentValues &&
      props.currentValues.starRatingFive &&
      props.currentValues.starRatingFive !== 5)
  ) {
    areRequiredImprovements = true;
  }

  let improvementsMinLength = areRequiredImprovements
    ? props.improvementsConditions?.minLength ?? 100
    : undefined;

  let namePrefix = "";
  if (props.namePrefix) {
    namePrefix = props.namePrefix + ".";
  }
  return (
    <>
      <Field
        label="Rating (5 being the highest)"
        name={namePrefix + "starRatingFive"}
        component={EBSelect}
        options={ratingHelpers.ratings}
        isMulti={false}
        mappingRequired={true}
        selectToApiMapper={mapRatingSelectToAPI}
        apiToSelectMapper={mapRatingAPIToSelect}
        required
      />
      <EBTextAreaV2
        label="Review (Shared publicly on profile)"
        helpText=""
        name={namePrefix + "text"}
        maxLength={1000}
        rows={4}
        enablePerformance={true}
        required
      />

      {shouldAskForImprovements ? (
        <EBTextAreaV2
          className="mt-2"
          label="What improvements would you suggest? (Shared privately and will not be shown publicly)"
          name={namePrefix + "improvements"}
          helpText=""
          maxLength={props.improvementsConditions?.maxLength ?? 1000}
          minLength={improvementsMinLength}
          rows={4}
          showCount={true}
          enablePerformance={true}
          required={areRequiredImprovements}
        />
      ) : null}
    </>
  );
}

interface ReviewFormProps {
  reviewSubmitApiLink: string;
  askForImprovements?: boolean;
}
export function ReviewForm(props: ReviewFormProps) {
  let initialValues: ReviewV1 = { text: "", starRatingFive: 5 };
  return (
    <div>
      <Formik
        initialValues={initialValues}
        onSubmit={(values, actions) => {
          actions.setSubmitting(true);
          axios
            .post(props.reviewSubmitApiLink, values)
            .then((response) => {
              reloadWindowCallback();
              message.success("Review saved!");
              actions.setSubmitting(false);
            })
            .catch((error) => {
              message.error("Could not save review!");
              actions.setSubmitting(false);
            });
        }}
      >
        {(formProps) => (
          <Form>
            <ReviewEdit
              currentValues={formProps.values}
              improvementsConditions={{
                askCondition: AskForImprovementsCondition.ALWAYS,
                requiredCondition:
                  ImprovementsRequiredCondition.WHEN_NOT_5_STAR,
                minLength: 100,
                maxLength: 1000,
              }}
            />
            <div className="">
              <button
                className="btn btn-sm btn-primary"
                type="submit"
                disabled={formProps.isSubmitting}
              >
                {formProps.isSubmitting ? "Saving... Please wait." : "Save"}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}
